import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const resolveGameIcon = (game) => {
  if (!game) return null;
  const rawUrl =
    (typeof game.imageUrl === "string" && game.imageUrl.trim()) ||
    (typeof game.iconUrl === "string" && game.iconUrl.trim()) ||
    (typeof game.logoUrl === "string" && game.logoUrl.trim()) ||
    (typeof game.image === "string" && game.image.trim()) ||
    (typeof game.icon === "string" && game.icon.trim()) ||
    null;
  return rawUrl || null;
};

const normalizeGame = (raw) => {
  if (!raw) return null;
  const idValue =
    raw.id ?? raw.gameId ?? raw.key ?? raw.slug ?? raw.code ?? raw.value ?? null;
  const nameValue = raw.name ?? raw.title ?? raw.label ?? null;
  const id = idValue !== null ? String(idValue).trim() : "";
  const name = nameValue !== null ? String(nameValue).trim() : "";
  if (!id || !name) return null;

  const format = raw.format || raw.gameFormat || null;
  const entryType = raw.entryType || raw.entry || raw.entry_type || null;
  const orderValue = raw.order ?? raw.position ?? raw.sortOrder ?? 0;
  const order = Number.isFinite(Number(orderValue)) ? Number(orderValue) : 0;
  const active =
    raw.active === false || raw.isActive === false || raw.disabled === true
      ? false
      : true;
  const imageUrl =
    (typeof raw.imageUrl === "string" && raw.imageUrl.trim()) ||
    (typeof raw.iconUrl === "string" && raw.iconUrl.trim()) ||
    (typeof raw.logoUrl === "string" && raw.logoUrl.trim()) ||
    (typeof raw.image === "string" && raw.image.trim()) ||
    null;

  return {
    id,
    name,
    format,
    entryType,
    order,
    active,
    imageUrl,
  };
};

const normalizeGameList = (list) => {
  if (!Array.isArray(list)) return [];
  const deduped = [];
  const seen = new Set();
  list.forEach((item) => {
    const normalized = normalizeGame(item);
    if (!normalized) return;
    if (seen.has(normalized.id)) return;
    seen.add(normalized.id);
    deduped.push(normalized);
  });
  return deduped;
};

const sortGames = (games) =>
  [...games].sort((a, b) => {
    const orderDiff = (a.order || 0) - (b.order || 0);
    if (orderDiff !== 0) return orderDiff;
    return a.name.localeCompare(b.name);
  });

const attachIcons = (games) =>
  games.map((game) => ({
    ...game,
    icon: resolveGameIcon(game),
  }));

const serializeGames = (games) =>
  games.map((game) => ({
    id: game.id,
    name: game.name,
    format: game.format || null,
    entryType: game.entryType || null,
    order: Number.isFinite(Number(game.order)) ? Number(game.order) : 0,
    active: game.active !== false,
    imageUrl: game.imageUrl || null,
  }));

const fetchRemoteGames = async () => {
  const configRef = doc(db, "config", "games");
  const configSnap = await getDoc(configRef);
  let gamesPayload = [];

  if (configSnap.exists()) {
    const data = configSnap.data() || {};
    const fromDoc = data.games || data.list || data.items;
    if (Array.isArray(fromDoc)) {
      gamesPayload = fromDoc;
    }
  }

  if (!gamesPayload.length) {
    const gamesSnap = await getDocs(collection(db, "games"));
    gamesPayload = gamesSnap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  }

  return gamesPayload;
};

const GameCatalogContext = createContext({
  games: [],
  activeGames: [],
  gameMap: {},
  loading: true,
  error: "",
  refreshGames: async () => {},
  saveGames: async () => {},
  getGameName: () => "Unknown",
  getGameConfig: () => null,
});

export const GameCatalogProvider = ({ children }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refreshGames = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const remote = await fetchRemoteGames();
      const normalized = normalizeGameList(remote);
      setGames(attachIcons(sortGames(normalized)));
    } catch (err) {
      console.error("Failed to load game catalog", err);
      setError("Failed to load game catalog.");
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveGames = useCallback(async (draft, options = {}) => {
    const normalized = normalizeGameList(draft);
    const sorted = sortGames(normalized);
    await setDoc(
      doc(db, "config", "games"),
      {
        games: serializeGames(sorted),
        updatedAt: serverTimestamp(),
        updatedBy: options.updatedBy || null,
      },
      { merge: true }
    );
    setGames(attachIcons(sorted));
    return sorted;
  }, []);

  useEffect(() => {
    refreshGames();
  }, [refreshGames]);

  const gameMap = useMemo(() => {
    return games.reduce((acc, game) => {
      acc[String(game.id)] = game;
      return acc;
    }, {});
  }, [games]);

  const activeGames = useMemo(
    () => games.filter((game) => game.active !== false),
    [games]
  );

  const getGameName = useCallback(
    (id) => {
      if (!id) return "Unknown";
      return gameMap[String(id)]?.name || "Unknown";
    },
    [gameMap]
  );

  const getGameConfig = useCallback(
    (id) => {
      if (!id) return null;
      return gameMap[String(id)] || null;
    },
    [gameMap]
  );

  const value = useMemo(
    () => ({
      games,
      activeGames,
      gameMap,
      loading,
      error,
      refreshGames,
      saveGames,
      getGameName,
      getGameConfig,
    }),
    [games, activeGames, gameMap, loading, error, refreshGames, saveGames, getGameName, getGameConfig]
  );

  return (
    <GameCatalogContext.Provider value={value}>
      {children}
    </GameCatalogContext.Provider>
  );
};

export default function useGameCatalog() {
  return useContext(GameCatalogContext);
}
